/**
 * Hook for voice dictation in Bird Chat.
 *
 * Primary path: Web Speech API (Chrome, Safari, Edge) — streams interim text
 * into the chat input in real time.
 *
 * Fallback path: MediaRecorder + POST /transcribe/ (Firefox, or when Web Speech
 * is unavailable) — records audio, sends to backend, returns final transcript.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  SpeechRecognition,
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from '../../types/speechRecognition';
import { getSpeechRecognitionCtor } from '../../types/speechRecognition';
import { apiService } from '../../services/api.service';
import { transcribeAudio } from '../../services/api/media.api';
import { ApiResponseError, unwrapApiResponse } from '../../services/api/http-client.types';
import { createVoiceRecorder, voiceFileName } from '../../utils/audioFormat';
import { describeMicrophoneError } from '../../utils/microphoneErrors';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useUnsavedWork } from '../../stores/unsavedWorkStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DictationMethod = 'web_speech' | 'backend';

export interface UseVoiceDictationOptions {
  /** Callback with the committed (final) transcript text to append to the input. */
  onTranscript: (text: string) => void;
  /** Callback with interim (in-progress) text for live preview. Web Speech only. */
  onInterimTranscript?: (text: string) => void;
  /** Called on error with a user-friendly message. */
  onError?: (message: string) => void;
  /** BCP-47 language tag. Default: browser language or 'en-US'. */
  lang?: string;
  /** Silence timeout in ms before auto-stopping. Default: 3000. */
  silenceTimeoutMs?: number;
  /**
   * Record and transcribe through Bower's own `/transcribe` only, never the
   * browser's speech service (which in Chrome streams the audio to its
   * vendor). For a PHI workspace. Default: false (Web Speech when available).
   */
  backendOnly?: boolean;
}

export interface UseVoiceDictationReturn {
  /** Whether any form of voice dictation is available in this browser. */
  isSupported: boolean;
  /** Which method will be used: 'web_speech' or 'backend'. */
  method: DictationMethod;
  /** Whether dictation is actively listening/recording. */
  isListening: boolean;
  /** Whether backend fallback is transcribing (post-recording). */
  isTranscribing: boolean;
  /** Start dictation. */
  startDictation: () => void;
  /** Stop dictation: finishes the take and delivers its transcript. */
  stopDictation: () => void;
  /**
   * Cancel dictation: nothing is sent, transcribed or reported as completed.
   * For a context change (a workspace switch) where finishing would ship the
   * captured audio under the NEW context, since the request resolves its
   * workspace at send time (review d0e4ab4 on #3703).
   */
  abortDictation: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function detectMethod(backendOnly: boolean): DictationMethod {
  return !backendOnly && getSpeechRecognitionCtor() ? 'web_speech' : 'backend';
}

function detectSupported(backendOnly: boolean): boolean {
  // Supported if either Web Speech API or MediaRecorder is available
  if (!backendOnly && getSpeechRecognitionCtor()) return true;
  if (typeof MediaRecorder !== 'undefined') return true;
  return false;
}

function getPlatform(): string {
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return 'ios';
  if (/Android/.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVoiceDictation({
  onTranscript,
  onInterimTranscript,
  onError,
  lang,
  silenceTimeoutMs = 3000,
  backendOnly = false,
}: UseVoiceDictationOptions): UseVoiceDictationReturn {
  const isSupported = detectSupported(backendOnly);
  const method = detectMethod(backendOnly);

  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // Listening (the fallback recorder holds audio in memory) and transcribing
  // (the text reaches the composer through this hook) both end with the
  // composer: tab close and a workspace switch ask first (on a switch the
  // composer aborts the dictation and drops its late transcript). 'app' scope:
  // the Bower panel survives in-app navigation, so leaving a note does not
  // interrupt it.
  useUnsavedWork(isListening || isTranscribing, 'a voice dictation', 'app');

  // Refs for stable callback access
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onInterimTranscriptRef = useRef(onInterimTranscript);
  onInterimTranscriptRef.current = onInterimTranscript;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Web Speech API refs
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  // Backend fallback refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  // Bumped by every way of giving up on a pending start: abortDictation, the
  // unmount cleanup, and a newer start (which supersedes it). A backend start
  // awaiting getUserMedia (the permission prompt can sit open) re-checks it and
  // stands down, releasing the stream, so none of those can leave a recorder
  // running on a live mic afterwards (reviews b22ca7d and 0b1ec32).
  const startGenerationRef = useRef(0);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Track whether we intentionally stopped (vs auto-stop from silence)
  const intentionalStopRef = useRef(false);

  const resolvedLang = lang ?? navigator.language ?? 'en-US';

  // -------------------------------------------------------------------
  // Cleanup helpers
  // -------------------------------------------------------------------

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = window.setTimeout(() => {
      // Auto-stop after silence
      if (recognitionRef.current) {
        intentionalStopRef.current = true;
        try {
          recognitionRef.current.stop();
        } catch {
          // already stopped
        }
      }
    }, silenceTimeoutMs);
  }, [clearSilenceTimer, silenceTimeoutMs]);

  // -------------------------------------------------------------------
  // Web Speech API path
  // -------------------------------------------------------------------

  const startWebSpeech = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;

    const recognition = new Ctor();
    const isAndroid = /Android/i.test(navigator.userAgent);

    // Android Chrome's Web Speech API is unreliable with continuous=true:
    // it duplicates final results across recognition restarts. Use
    // single-shot mode and manually restart after each final result.
    recognition.continuous = !isAndroid;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = resolvedLang;

    let committedText = '';
    let emittedFinalLength = 0;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      resetSilenceTimer();

      let allFinalText = '';
      let interimText = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) {
          allFinalText += transcript;
        } else {
          interimText += transcript;
        }
      }

      // Emit only the NEW portion of final text. On Android single-shot
      // mode, results reset each cycle so emittedFinalLength resets via
      // the restart flow. On desktop continuous mode, results accumulate
      // and we use length tracking to emit only the delta.
      if (allFinalText.length > emittedFinalLength) {
        const newText = allFinalText.slice(emittedFinalLength);
        emittedFinalLength = allFinalText.length;
        committedText += newText;
        onTranscriptRef.current(newText);

        // On Android, restart recognition for the next utterance
        if (isAndroid && recognitionRef.current) {
          // Reset tracking — the results list starts fresh after restart
          emittedFinalLength = 0;
          try {
            recognitionRef.current.stop();
            // onend will fire → we restart there
          } catch {
            // already stopped
          }
        }
      }

      // Show interim text (not yet committed)
      onInterimTranscriptRef.current?.(interimText);
    };

    recognition.onend = () => {
      clearSilenceTimer();
      // Clear any lingering interim text
      onInterimTranscriptRef.current?.('');

      // On Android single-shot mode, auto-restart unless the user stopped
      if (isAndroid && !intentionalStopRef.current && recognitionRef.current) {
        try {
          recognition.start();
          resetSilenceTimer();
          return; // Don't mark as stopped — still listening
        } catch {
          // Failed to restart — fall through to stop
        }
      }

      setIsListening(false);

      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);

      if (committedText.length > 0 || intentionalStopRef.current) {
        safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_COMPLETED, {
          platform: getPlatform(),
          method: 'web_speech',
          duration_seconds: duration,
          transcript_length: committedText.length,
        } as Record<string, unknown>);
      }

      recognitionRef.current = null;
      intentionalStopRef.current = false;
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      clearSilenceTimer();

      switch (event.error) {
        case 'no-speech':
          // Normal silence — will auto-restart or stop via onend
          return;
        case 'aborted':
          // We called stop() — ignore
          return;
        case 'not-allowed': {
          setIsListening(false);
          recognitionRef.current = null;
          onInterimTranscriptRef.current?.('');
          const msg = 'Microphone access denied. Enable it in browser settings.';
          onErrorRef.current?.(msg);
          safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_FAILED, {
            platform: getPlatform(),
            method: 'web_speech',
            error_type: 'permission_denied',
          } as Record<string, unknown>);
          return;
        }
        default: {
          setIsListening(false);
          recognitionRef.current = null;
          onInterimTranscriptRef.current?.('');
          onErrorRef.current?.("Couldn't understand that. Try again or type.");
          safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_FAILED, {
            platform: getPlatform(),
            method: 'web_speech',
            error_type: event.error,
          } as Record<string, unknown>);
        }
      }
    };

    recognitionRef.current = recognition;
    startTimeRef.current = Date.now();
    intentionalStopRef.current = false;
    setIsListening(true);

    try {
      recognition.start();
      resetSilenceTimer();

      safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_STARTED, {
        platform: getPlatform(),
        method: 'web_speech',
      } as Record<string, unknown>);
    } catch {
      setIsListening(false);
      recognitionRef.current = null;
      onErrorRef.current?.('Failed to start voice dictation.');
    }
  }, [resolvedLang, resetSilenceTimer, clearSilenceTimer]);

  const stopWebSpeech = useCallback(() => {
    clearSilenceTimer();
    if (recognitionRef.current) {
      intentionalStopRef.current = true;
      try {
        recognitionRef.current.stop();
      } catch {
        // already stopped
      }
    }
  }, [clearSilenceTimer]);

  // -------------------------------------------------------------------
  // Backend fallback path (MediaRecorder + POST /transcribe/)
  // -------------------------------------------------------------------

  const startBackendFallback = useCallback(async () => {
    // Acquisition gets its own try, mirroring useVoiceRecording. Sharing one
    // catch with the recorder setup below is the defect this change set out to
    // fix on the sibling: a codec/capability failure gets described as a
    // microphone-acquisition problem, and the stream opened here is never
    // released, so the mic indicator stays lit until unmount.
    const generation = ++startGenerationRef.current;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      if (startGenerationRef.current !== generation) return;
      // Was a hardcoded 'permission_denied' for every cause, so the event
      // ASSERTED a denial that may never have happened — a busy headset and an
      // unsupported recorder both reported as the user refusing us. Same
      // describer as useVoiceRecording so the two surfaces can't drift.
      const { title, description, reason } = describeMicrophoneError(error);
      onErrorRef.current?.(`${title}. ${description}`);
      safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_FAILED, {
        platform: getPlatform(),
        method: 'backend',
        error_type: reason,
      } as Record<string, unknown>);
      return;
    }
    if (startGenerationRef.current !== generation) {
      // Aborted while the microphone was being acquired.
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    try {
      streamRef.current = stream;

      // Shared factory — same codec/bitrate decisions as voice notes.
      const { recorder, mimeType: actualMimeType } = createVoiceRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Release mic
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        if (audioBlob.size < 1000) {
          setIsListening(false);
          onErrorRef.current?.('Recording too short. Try again.');
          return;
        }

        setIsListening(false);
        setIsTranscribing(true);

        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);

        try {
          const formData = new FormData();
          formData.append(
            'audio',
            new File([audioBlob], voiceFileName('dictation', actualMimeType), {
              type: actualMimeType,
            }),
          );
          formData.append('language', resolvedLang);
          formData.append('punctuation', 'true');

          // unwrap: a resolved { success: false } throws ApiResponseError so
          // the catch below can put the backend's error code (quota, size,
          // provider outage) on the VOICE_DICTATION_FAILED event instead of a
          // generic 'transcription_failed'.
          const data = unwrapApiResponse(
            await transcribeAudio(apiService, formData),
            'Transcription failed',
          ) as { transcript?: string } | undefined;
          const transcript = data?.transcript;

          if (transcript) {
            onTranscriptRef.current(transcript);
            safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_COMPLETED, {
              platform: getPlatform(),
              method: 'backend',
              duration_seconds: duration,
              transcript_length: transcript.length,
            } as Record<string, unknown>);
          } else {
            throw new Error('No transcript returned');
          }
        } catch (err) {
          onErrorRef.current?.('Transcription failed. Try again or type.');
          safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_FAILED, {
            platform: getPlatform(),
            method: 'backend',
            error_type:
              err instanceof ApiResponseError && err.code ? err.code : 'transcription_failed',
          } as Record<string, unknown>);
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start(100);
      startTimeRef.current = Date.now();
      setIsListening(true);

      safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_STARTED, {
        platform: getPlatform(),
        method: 'backend',
      } as Record<string, unknown>);
    } catch (error) {
      // Recorder/codec setup, not acquisition — that returned above. Release
      // the stream we opened, or the mic indicator stays lit until unmount.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      onErrorRef.current?.(
        "Recording isn't supported on this device. Your browser could not start an audio recorder.",
      );
      safeAnalytics.track(ANALYTICS_EVENTS.VOICE_DICTATION_FAILED, {
        platform: getPlatform(),
        method: 'backend',
        // A stage, not a DOMException name: this is a capability problem, and
        // labelling it with the describer's generic branch would put it in the
        // same bucket as a genuine acquisition failure.
        error_type: 'recorder_setup',
        error_name: error instanceof Error ? error.name : 'UnknownError',
      } as Record<string, unknown>);
    }
  }, [resolvedLang]);

  const stopBackendFallback = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop(); // triggers onstop → transcribe flow
    }
  }, []);

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  const startDictation = useCallback(() => {
    if (isListening || isTranscribing) return;

    if (method === 'web_speech') {
      startWebSpeech();
    } else {
      void startBackendFallback();
    }
  }, [isListening, isTranscribing, method, startWebSpeech, startBackendFallback]);

  const stopDictation = useCallback(() => {
    if (method === 'web_speech') {
      stopWebSpeech();
    } else {
      stopBackendFallback();
    }
  }, [method, stopWebSpeech, stopBackendFallback]);

  const abortDictation = useCallback(() => {
    startGenerationRef.current += 1;
    clearSilenceTimer();
    const recognition = recognitionRef.current;
    if (recognition) {
      intentionalStopRef.current = true;
      recognition.onresult = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // already stopped
      }
      recognitionRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Detach BEFORE stopping: onstop is what uploads to /transcribe.
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    onInterimTranscriptRef.current?.('');
    setIsListening(false);
  }, [clearSilenceTimer]);

  // -------------------------------------------------------------------
  // Cleanup on unmount
  // -------------------------------------------------------------------

  useEffect(() => {
    return () => {
      startGenerationRef.current += 1;
      clearSilenceTimer();

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // Ignore
        }
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [clearSilenceTimer]);

  return {
    isSupported,
    method,
    isListening,
    isTranscribing,
    startDictation,
    stopDictation,
    abortDictation,
  };
}
